import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    apiReferenceV2: {
      insertApiReferenceV2: (attrs?: {
        method?: ApiReferenceV2Method;
        path?: string;
        title?: string;
        description?: string;
        useWhen?: string;
      }) => ReturnType;
      updateApiReferenceV2: (attrs: {
        method?: ApiReferenceV2Method;
        path?: string;
        title?: string;
        description?: string;
        useWhen?: string;
      }) => ReturnType;
    };
  }
}

export type ApiReferenceV2Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

export interface ApiReferenceV2Options {
  HTMLAttributes: Record<string, any>;
  view: any;
}

const ALLOWED_METHODS: ApiReferenceV2Method[] = [
  'get',
  'post',
  'put',
  'patch',
  'delete',
];

function normalizeMethod(v: unknown): ApiReferenceV2Method {
  if (typeof v !== 'string') return 'get';
  const lower = v.toLowerCase() as ApiReferenceV2Method;
  return ALLOWED_METHODS.includes(lower) ? lower : 'get';
}

function strAttr(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

export const ApiReferenceV2 = Node.create<ApiReferenceV2Options>({
  name: 'apiReferenceV2',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      view: null,
    };
  },

  addAttributes() {
    return {
      method: {
        default: 'get' as ApiReferenceV2Method,
        parseHTML: (el) => normalizeMethod(el.getAttribute('data-method')),
        renderHTML: (attrs) => ({
          'data-method': normalizeMethod(attrs.method),
        }),
      },
      path: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-path') ?? '',
        renderHTML: (attrs) => ({ 'data-path': strAttr(attrs.path) }),
      },
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') ?? '',
        renderHTML: (attrs) => ({ 'data-title': strAttr(attrs.title) }),
      },
      description: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-description') ?? '',
        renderHTML: (attrs) => ({
          'data-description': strAttr(attrs.description),
        }),
      },
      useWhen: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-use-when') ?? '',
        renderHTML: (attrs) => ({
          'data-use-when': strAttr(attrs.useWhen),
        }),
      },
      showTitle: {
        default: true,
        parseHTML: (el) => el.getAttribute('data-show-title') !== 'false',
        renderHTML: (attrs) => ({
          'data-show-title': attrs.showTitle === false ? 'false' : 'true',
        }),
      },
      showDescription: {
        default: true,
        parseHTML: (el) => el.getAttribute('data-show-description') !== 'false',
        renderHTML: (attrs) => ({
          'data-show-description':
            attrs.showDescription === false ? 'false' : 'true',
        }),
      },
      showUseWhen: {
        default: true,
        parseHTML: (el) => el.getAttribute('data-show-use-when') !== 'false',
        renderHTML: (attrs) => ({
          'data-show-use-when':
            attrs.showUseWhen === false ? 'false' : 'true',
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="api-reference-v2"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': 'api-reference-v2' },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
    ];
  },

  addCommands() {
    return {
      insertApiReferenceV2:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              method: normalizeMethod(attrs?.method),
              path: strAttr(attrs?.path),
              title: strAttr(attrs?.title),
              description: strAttr(attrs?.description),
              useWhen: strAttr(attrs?.useWhen),
            },
          }),
      updateApiReferenceV2:
        (attrs) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, {
            ...(attrs.method !== undefined && {
              method: normalizeMethod(attrs.method),
            }),
            ...(attrs.path !== undefined && { path: attrs.path }),
            ...(attrs.title !== undefined && { title: attrs.title }),
            ...(attrs.description !== undefined && {
              description: attrs.description,
            }),
            ...(attrs.useWhen !== undefined && { useWhen: attrs.useWhen }),
          }),
    };
  },

  addNodeView() {
    if (!this.options.view) return null;
    return ReactNodeViewRenderer(this.options.view);
  },
});
